import IconBlueLogo from '@listening/assets/astro/icons/icon-blue-logo.svg?react';
import { Link } from '@tanstack/react-router';
import { use } from 'react';
import {
  CtaButton,
  CtaButtonContext,
} from '@/components/astro/cta-button/cta-button.tsx';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import { clientEnv } from '@/env';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';

import { AuthNavLink } from './subcomponents/auth-nav-link';
import { MobileNavCollapsible } from './subcomponents/mobile-nav-collapsible';

// `inRouter`/`reloadDocument` existed for `/podcast` alone — `23d166c27` marked
// it `inRouter: false` to force a hard link, on the belief it was "served
// outside this route tree". It wasn't served at all: `a33295971` ("rm podcast
// code", 2025-12-01) had already deleted the routes and left the nav link
// behind, so the hard link went to a 404. With the entry gone every remaining
// one is a real route, `inRouter` is a constant `true`, and `reloadDocument`
// a constant `false` — which is its default.
const navigationLinks = [
  { href: '/downloads', label: () => m.nav_apps() },
  { href: '/pricing', label: () => m.nav_pricing() },
  { href: '/features', label: () => m.nav_features() },
  ...(clientEnv.publicToolsEnabled
    ? [{ href: '/tools', label: () => 'Tools' }]
    : []),
  { href: '/business', label: () => m.nav_business() },
];

export function NavBar({
  logoLinkHref = '/',
  sticky = false,
  variant = 'default',
  showLinks = true,
  ctaLabel,
}: {
  logoLinkHref?: string | undefined;
  sticky?: boolean;
  variant?: 'default' | 'bordered';
  showLinks?: boolean;
  ctaLabel?: string;
}) {
  const { isInFreePlanCountry } = useFreePlan();
  const links = showLinks ? navigationLinks : [];
  // Spread what we inherit: this provider only means to set a label, and
  // replacing the value outright would drop a page-level `href` override and
  // leave the nav CTA pointing somewhere the rest of the page no longer does.
  const inheritedCta = use(CtaButtonContext);

  return (
    <CtaButtonContext
      value={{
        ...inheritedCta,
        label:
          ctaLabel ??
          (isInFreePlanCountry ? m.nav_cta_free() : m.common_try_now()),
      }}
    >
      <nav
        aria-label={m.nav_aria_main_navigation()}
        className={`inset-x-0 top-0 z-50 mx-auto w-full max-w-[1600px] ${
          sticky
            ? 'sticky border-b border-neutral-200 bg-white'
            : variant === 'bordered'
              ? 'border-b border-neutral-200 bg-white'
              : ''
        }`}
        itemScope
        itemType='https://schema.org/SiteNavigationElement'
      >
        <div className='py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6 xl:px-[72px]'>
          <div className='flex items-center'>
            <MobileNavCollapsible>
              <div
                aria-label={m.nav_aria_mobile_menu()}
                className='h-full px-4 pt-24 sm:px-6 sm:pt-32'
                role='menu'
              >
                <div className='flex flex-col space-y-6 sm:space-y-8'>
                  {links.map(({ href, label }) => (
                    <Link
                      className='text-lg font-medium text-white transition-colors hover:text-white/80 sm:text-xl'
                      itemProp='url'
                      key={href}
                      role='menuitem'
                      to={localizeHref(href)}
                    >
                      <span itemProp='name'>{label()}</span>
                    </Link>
                  ))}
                  <AuthNavLink
                    className='text-lg font-medium text-white transition-colors hover:text-white/80 sm:text-xl'
                    role='menuitem'
                  />
                </div>
              </div>
            </MobileNavCollapsible>

            <div className='ml-2 flex shrink-0 items-center md:ml-0'>
              <Link
                aria-label={m.nav_aria_home_link()}
                className='z-51 flex items-center gap-1.5 text-xl font-semibold text-black sm:gap-2 sm:text-2xl lg:text-3xl'
                id='brand-title'
                itemProp='url'
                to={localizeHref(logoLinkHref)}
              >
                <IconBlueLogo
                  aria-hidden='true'
                  className='size-6 sm:size-8 lg:size-9'
                  role='img'
                />
                <span itemProp='name'>{m.nav_brand_name()}</span>
              </Link>

              <div
                aria-label={m.nav_aria_desktop_menu()}
                className='relative z-51 ml-8 hidden items-center gap-6 md:flex md:gap-3 lg:ml-12 lg:gap-8'
                role='menubar'
              >
                {links.map(({ href, label }) => (
                  <Link
                    className='text-sm font-medium text-black transition-colors hover:text-black/80 lg:text-base'
                    itemProp='url'
                    key={href}
                    role='menuitem'
                    to={localizeHref(href)}
                  >
                    <span itemProp='name'>{label()}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div
              aria-label={m.nav_aria_cta_buttons()}
              className='relative z-51 ml-auto hidden items-center gap-4 md:flex md:gap-2 lg:gap-6'
              role='group'
            >
              <AuthNavLink
                align='end'
                className='text-sm font-medium text-black hover:text-black/80 lg:text-base'
              />
              <CtaButton
                className='rounded-full bg-royal-50 px-8 py-7 text-xl font-light text-white hover:bg-black/80 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden'
                key='cta'
                variant='gradient'
              />
            </div>

            <div
              aria-label={m.nav_aria_mobile_cta()}
              className='relative z-51 ml-auto md:hidden'
              role='group'
            >
              <CtaButton
                className={`mr-2 rounded-full bg-royal-50 font-light text-white hover:bg-black/80 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden sm:mr-auto ${
                  isInFreePlanCountry
                    ? 'px-4 py-3 text-sm sm:px-6 sm:py-5 sm:text-base'
                    : 'wf-max-sm:p-2 wf-max-sm:text-xs px-8 py-7 text-xl'
                }`}
              />
            </div>
          </div>
        </div>
      </nav>
    </CtaButtonContext>
  );
}
